import React, { useEffect, useRef } from 'react';
import ConditionEditor from './ConditionEditor';
import InsertRow from './InsertRow';
import { conditionOf, newCondition } from './formConditions';
import { flattenFields, flattenSections, sectionFields, sectionTitle } from './formSections';
import { ACTION_BTN, DANGER_ICON_BTN, GRIP_ICON_BTN } from './ui';

const TrashIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
  </svg>
);

const GripIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <circle cx="7" cy="5" r="1.5" />
    <circle cx="13" cy="5" r="1.5" />
    <circle cx="7" cy="10" r="1.5" />
    <circle cx="13" cy="10" r="1.5" />
    <circle cx="7" cy="15" r="1.5" />
    <circle cx="13" cy="15" r="1.5" />
  </svg>
);

// How far down the app's own header sits, and how far each level of pinned
// title starts below the one it is filed under. A shade less than a title row
// is tall, so consecutive pinned titles overlap by a pixel or two rather than
// leaving a slot for the page to scroll through between them.
const APP_HEADER_REM = 4;
const HEADER_HEIGHT_REM = 3.25;
// Past this depth the titles would fill the screen instead of labelling it, so
// the innermost ones share the last slot rather than claiming a new one each.
const MAX_PINNED_DEPTH = 2;

// Where a title comes to rest, and what it passes when it doesn't. A pinned
// title can still be pushed back up by its own section leaving the screen, so
// an outer one has to sit above the titles inside it: a subsection's title then
// slides away under the chapter it belongs to instead of across it. Below the
// app's own nav either way, which owns z-50.
const pinnedTop = (depth) =>
  `${APP_HEADER_REM + Math.min(depth, MAX_PINNED_DEPTH) * HEADER_HEIGHT_REM}rem`;

const pinnedZ = (depth) => Math.max(21, 24 - depth);

// A section in the Form Builder: a named box around a run of cards, drawn as
// the cards it holds nested inside its own. Those cards are questions or
// sections in turn — `renderEntry` draws whichever it is, and a subsection
// comes back through here one level deeper.
//
// The title row is sticky. On a survey long enough to need sections, the title
// is the one thing you can't work out from what's on screen — which is exactly
// what scrolls off first — so it stays pinned under the app's own header, and
// under the titles of the sections this one is inside, until the next section
// at its level takes over.
//
// Below the cards sits a strip that is the section's "and at the end of this
// one" drop target: without it, a card dropped past the last one is
// indistinguishable from one dropped after the whole section. It doubles as the
// body of an empty section, where it is the only place a card can land.
//
// The gaps between the cards carry the same insert controls as the gaps
// outside. A question added in one lands in this section, right there; a
// section started in one is a subsection, taking the questions below the gap
// with it the same way a section at the top level does. The card is padded
// deeper at the foot than at the head to leave one of those gaps room: the
// controls for the gap after the section sit directly below the card, and a few
// pixels borrowed from whatever follows is not enough to hold them.
//
// A section can be conditional the same way a question can. The switch is a
// Conditional box in the title row, where it stays in view while the section
// is being worked on; the condition itself is written out as a sentence across
// the top of the section's body — "Only show this section when …" — so it
// reads as the first thing in the section, ahead of everything it governs.
// `formEntries` is the whole form as a tree, and `conditionCheck` this
// section's result from formConditions.checkConditions.
const SectionEditorCard = ({
  section,
  depth = 0,
  autoFocus,
  isDragging,
  dragActive,
  entryProps,
  handleProps,
  headerProps,
  bodyProps,
  endProps,
  formEntries,
  conditionCheck,
  setCondition,
  updateSectionName,
  removeSection,
  addFieldToSection,
  addFieldAt,
  addSection,
  renderEntry,
}) => {
  const entries = sectionFields(section);
  const questionCount = flattenFields(entries).length;
  const sectionCount = flattenSections(entries).length;
  const title = sectionTitle(section);
  const nested = depth > 0;
  const kind = nested ? 'Subsection' : 'Section';
  const conditional = !!conditionOf(section);
  const nameRef = useRef(null);

  // A section arrives unnamed, so the cursor goes where the author is about to
  // type. Only when it is first created: re-focusing on any later render would
  // yank the caret back out of whatever they moved on to.
  useEffect(() => {
    if (autoFocus) nameRef.current?.focus();
  }, [autoFocus]);

  const insertRow = (index, place) => (
    <InsertRow
      onAddQuestion={() => addFieldAt(section.id, index)}
      onAddSection={() => addSection(section.id, index)}
      place={place}
    />
  );

  return (
    <div
      {...entryProps}
      id={`entry-${section.id}`}
      className={`rounded-xl border bg-purple-500/[0.07] px-3 pt-3 pb-5 transition-colors ${
        isDragging ? 'border-purple-400 ring-2 ring-purple-500/50 opacity-70' : 'border-purple-500/40'
      }`}
    >
      <div
        {...headerProps}
        style={{ top: pinnedTop(depth), zIndex: pinnedZ(depth) }}
        className="group/section sticky flex items-center gap-2 rounded-lg border border-purple-500/30 bg-gray-900/95 backdrop-blur-sm px-2 py-2"
      >
        <span
          {...handleProps}
          role="button"
          tabIndex={0}
          aria-label={`Reorder the ${section.name?.trim() || 'untitled'} section. Press the up or down arrow keys to move it and everything in it.`}
          title="Drag to move this section and everything in it"
          className={`${GRIP_ICON_BTN} shrink-0 cursor-grab active:cursor-grabbing select-none opacity-50 group-hover/section:opacity-100 focus-visible:opacity-100 transition-opacity`}
        >
          <GripIcon />
        </span>
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-purple-300/80">
          {kind}
        </span>
        <input
          ref={nameRef}
          type="text"
          value={section.name || ''}
          onChange={(e) => updateSectionName(section.id, e.target.value)}
          placeholder={`${kind} name`}
          aria-label={`${kind} name`}
          className={`flex-1 min-w-0 bg-transparent border-b border-transparent hover:border-gray-600 focus:border-purple-500 focus:outline-none px-1 py-1 font-semibold text-white placeholder-gray-500 ${
            nested ? 'text-base' : 'text-lg'
          }`}
        />
        {/* On a phone the count steps aside altogether: the questions it
            counts are right there below it, and the name and the Conditional
            box need the room more. */}
        <span className="hidden sm:inline shrink-0 text-xs text-gray-400 tabular-nums">
          {questionCount} question{questionCount === 1 ? '' : 's'}
          {sectionCount > 0 && ` · ${sectionCount} section${sectionCount === 1 ? '' : 's'}`}
        </span>
        {/* Squeezed the same way: on a narrow screen the box keeps its place
            and the word steps out of sight, though not out of reach of a
            screen reader. */}
        <label
          className="shrink-0 flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none"
          title={`Only show this ${kind.toLowerCase()} when an answer elsewhere on the form matches`}
        >
          <input
            type="checkbox"
            checked={conditional}
            onChange={(e) => setCondition(section.id, e.target.checked ? newCondition(null) : null)}
          />
          <span className="sr-only sm:not-sr-only">Conditional</span>
        </label>
        <button
          type="button"
          onClick={() => removeSection(section)}
          className={`${DANGER_ICON_BTN} shrink-0`}
          title="Delete this section"
          aria-label={`Delete the ${section.name?.trim() || 'untitled'} section`}
        >
          <TrashIcon />
        </button>
      </div>

      <div {...bodyProps} className="mt-3">
        {/* Above the first card the gap's own insert row keeps them apart; an
            empty section has only the strip below, so the space comes from
            here. */}
        {conditional && (
          <div className={entries.length > 0 ? '' : 'mb-3'}>
            <ConditionEditor
              entry={section}
              entries={formEntries}
              subject={kind.toLowerCase()}
              problem={conditionCheck?.problem}
              onChange={(condition) => setCondition(section.id, condition)}
            />
          </div>
        )}
        {entries.length > 0 && insertRow(0, `inside “${title}”, above everything in it`)}
        {entries.map((entry, index) => (
          <React.Fragment key={entry.id}>
            {renderEntry(entry, depth + 1)}
            {insertRow(index + 1, `inside “${title}”, here`)}
          </React.Fragment>
        ))}
        <div
          {...endProps}
          className={`flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2 text-sm transition-colors ${
            dragActive
              ? 'border-purple-400/70 bg-purple-500/10 text-purple-100'
              : 'border-gray-600/70 text-gray-500'
          }`}
        >
          <span>
            {entries.length === 0
              ? 'Nothing in this section yet.'
              : 'Drop a card here to keep it in this section.'}
          </span>
          <button
            type="button"
            onClick={() => addFieldToSection(section.id)}
            className={`${ACTION_BTN} shrink-0`}
          >
            <PlusIcon />
            Add question
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionEditorCard;
